import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '@/lib/firebase/config';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
}

export default function ProtectedRoute({ children, roles = [] }: ProtectedRouteProps) {
  const location = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if Firebase auth is properly initialized
        if (!auth || !auth.currentUser) {
          setHasPermission(false);
          setLoading(false);
          return;
        }
        
        if (roles.length > 0) {
          // Check user roles
          const userRef = doc(db, 'users', auth.currentUser.uid);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            // Check seller role
            if (roles.includes('seller')) {
              if (userData.stripeAccountId && userData.stripeAccountStatus === 'active') {
                setHasPermission(true);
              } else if (userData.stripeAccountId && userData.stripeAccountStatus !== 'active') {
                toast({
                  title: "Account Setup Incomplete",
                  description: "Please complete your seller account setup to access this page.",
                  variant: "destructive",
                });
                setHasPermission(false);
              } else {
                setHasPermission(false);
              }
              return;
            }
            
            // Check admin role
            if (roles.includes('admin') && userData.role === 'admin') {
              setHasPermission(true);
              return;
            }
            
            // If we get here and roles were required but not matched
            if (roles.length > 0) {
              setHasPermission(false);
              toast({
                title: "Access Denied",
                description: "You don't have permission to access this page.",
                variant: "destructive",
              });
            }
          } else {
            setHasPermission(false);
          }
        } else {
          // Just need to be logged in
          setHasPermission(true);
        }
      } catch (error) {
        console.error('Error checking permissions:', error);
        toast({
          title: "Error",
          description: "There was a problem verifying your access. Please try again.",
          variant: "destructive",
        });
        setHasPermission(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, [roles, toast]);
  
  if (loading) {
    return (
      <div className="h-[calc(100vh-4rem)] flex flex-col items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-sm text-muted-foreground">Checking permissions...</p>
      </div>
    );
  }
  
  if (!hasPermission) {
    // Save the attempted URL to redirect back after login
    const redirectPath = roles.includes('seller') ? "/seller/onboarding" : "/auth";
    const searchParams = new URLSearchParams();
    if (location.pathname !== '/') {
      searchParams.set('redirect', location.pathname + location.search);
    }
    const redirectUrl = `${redirectPath}?${searchParams.toString()}`;
    
    return <Navigate to={redirectUrl} replace />;
  }
  
  return <>{children}</>;
} 